import React, { useState } from "react";
import { toast } from "sonner";
import { Users, Trash2, MailCheck, ChevronDown, ChevronUp, Download } from "lucide-react";
import { useApi } from "../hooks/useApi";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { fmtDate } from "../lib/dates";

const STATE = { active: "bg-palm/20 text-palm", pending: "bg-gold/30 text-crimson", unsubscribed: "bg-stone-200 text-stone-600" };
const FILTERS = [["all", "All"], ["active", "Active"], ["pending", "Pending"], ["unsubscribed", "Unsubscribed"]];
const err = (e) => toast.error(formatError(e.response?.data?.detail) || "Something went wrong");

function SubscriberRow({ s, onChanged }) {
  const remove = async () => {
    if (!window.confirm(`Remove ${s.email} from the newsletter list?`)) return;
    try { await api.delete(`/admin/newsletter-subscribers/${s.id}`); toast.success("Removed"); onChanged(); } catch (e) { err(e); }
  };
  const resend = async () => { try { await api.post(`/admin/newsletter-subscribers/${s.id}/resend-confirmation`); toast.success("Confirmation email sent"); onChanged(); } catch (e) { err(e); } };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2" data-testid={`sub-row-${s.id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-crimson">{s.email}</p>
        <p className="text-xs text-muted-foreground">{s.name || "—"} · joined {fmtDate(s.created_at)}{s.confirmed_at ? ` · confirmed ${fmtDate(s.confirmed_at)}` : ""}{s.reminded_at ? " · reminded" : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATE[s.state]}`} data-testid={`sub-state-${s.id}`}>{s.state}</span>
        {s.state !== "active" && <Button size="sm" variant="ghost" onClick={resend} title="Resend confirmation" data-testid={`sub-resend-${s.id}`}><MailCheck size={14} /></Button>}
        <Button size="sm" variant="ghost" onClick={remove} className="text-crimson" data-testid={`sub-remove-${s.id}`}><Trash2 size={14} /></Button>
      </div>
    </div>
  );
}

export function SubscriberList() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const { data, reload } = useApi(open ? `/admin/newsletter-subscribers?q=${encodeURIComponent(q)}&status=${status}` : null);
  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/newsletter-subscribers/export.csv?status=${status}`, { responseType: "blob" });
      const href = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = href; a.download = `newsletter-subscribers-${status}.csv`; a.click(); URL.revokeObjectURL(href);
    } catch (e) { err(e); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-gold/30" data-testid="subscriber-list">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-4 py-3 text-left" data-testid="subscriber-list-toggle">
        <span className="flex items-center gap-2 font-semibold text-crimson"><Users size={16} className="text-gold" /> Email subscribers</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="border-t border-gold/15 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="w-64" data-testid="subscriber-search" />
            <div className="flex gap-1">{FILTERS.map(([k, l]) => <button key={k} onClick={() => setStatus(k)} className={`rounded-full px-3 py-1 text-xs ${status === k ? "bg-crimson text-parchment" : "bg-parchment-card text-crimson"}`} data-testid={`subscriber-filter-${k}`}>{l}</button>)}</div>
            {data && <span className="ml-auto text-xs text-muted-foreground" data-testid="subscriber-count">{data.length} shown</span>}
            <Button size="sm" variant="outline" onClick={exportCsv} className="gap-1" data-testid="subscriber-export"><Download size={14} /> Export CSV</Button>
          </div>
          <div className="mt-2 max-h-96 divide-y divide-gold/15 overflow-y-auto rounded-xl bg-white/60">
            {data && data.length === 0 && <p className="p-3 text-sm text-muted-foreground" data-testid="subscriber-empty">No subscribers match.</p>}
            {(data || []).map((s) => <SubscriberRow key={s.id} s={s} onChanged={reload} />)}
          </div>
        </div>
      )}
    </div>
  );
}
